/**
 * ObjectViewer — a floating, always-on-top schema palette (toggle: Ctrl+F12).
 *
 * Built on <FloatingPanel> (the draggable, dim-on-blur, non-modal chassis); this
 * component supplies only the schema content + the conn/db context. The editor
 * behind it stays usable. Context-aware: it follows the focused query editor's
 * connection + database, and the Columns tab shows the table whose name is under
 * the cursor (enum columns list their values). Tabs: Columns · Triggers ·
 * Routines · MySQL functions, plus a search across all of them. Copy: columns →
 * `c1, c2, …`; trigger → its definition; routine / MySQL function → its
 * call-ready signature. Connection/database can also be picked manually.
 *
 * Props: { open, onClose }.
 */
import { createSignal, createMemo, createResource, createEffect, onCleanup, For, Show } from 'solid-js';
import FloatingPanel from './FloatingPanel.jsx';
import { api } from '../lib/api.js';
import { fetchObjectCatalog } from '../lib/objectcatalog.js';
import { FUNCTIONS } from '../lib/sqlfunctions.js';
import { readActiveEditor } from '../lib/editorcontext.js';
import { toast } from '../lib/toast.js';

export default function ObjectViewer(props) {
  const [pinned, setPinned] = createSignal(false); // user manually chose conn/db
  const [connId, setConnId] = createSignal('');
  const [database, setDatabase] = createSignal('');
  const [dbList, setDbList] = createSignal([]);
  const [ctxWord, setCtxWord] = createSignal('');   // table name under the cursor
  const [manualTable, setManualTable] = createSignal(''); // overrides ctxWord in Columns

  const [tab, setTab] = createSignal('columns');
  const [search, setSearch] = createSignal('');
  const [selTrigger, setSelTrigger] = createSignal(null);
  const [selRoutine, setSelRoutine] = createSignal(null);
  const [selFunc, setSelFunc] = createSignal(null);

  const [conns] = createResource(() => (props.open ? 'x' : null), async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });
  const loadDbs = async (id) => {
    if (!id) { setDbList([]); return; }
    try { setDbList(((await api.get(`/api/connections/${id}/databases`)).databases || []).map((d) => d.name)); }
    catch { setDbList([]); }
  };
  const connName = () => (conns() || []).find((c) => String(c.id) === String(connId()))?.name || '';

  // Catalog for the active (conn, db).
  const [catalog] = createResource(
    () => (props.open && connId() && database() ? { id: connId(), db: database() } : null),
    ({ id, db }) => fetchObjectCatalog(id, db),
  );

  // Follow the focused editor while open (unless the user pinned a conn/db).
  const sync = () => {
    const c = readActiveEditor();
    if (!c) return;
    setCtxWord(c.word || '');
    if (!pinned()) {
      if (c.connectionId && String(c.connectionId) !== String(connId())) { setConnId(c.connectionId); loadDbs(c.connectionId); }
      if (c.database && c.database !== database()) setDatabase(c.database);
    }
  };
  createEffect(() => {
    if (!props.open) return;
    sync();
    const t = setInterval(sync, 300);
    onCleanup(() => clearInterval(t));
  });

  const copy = async (text, label) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); toast.success(`${label || 'Copied'} copied`); }
    catch { toast.error('Copy failed'); }
  };

  // The table shown in the Columns tab: a manual pick, else the cursor word.
  const colTable = createMemo(() => {
    const cat = catalog(); if (!cat) return null;
    const want = (manualTable() || ctxWord() || '').toLowerCase();
    if (!want) return null;
    return cat.tables.find((t) => t.name.toLowerCase() === want) || null;
  });

  // Unified search across columns, triggers (name + body), routines, MySQL fns.
  const results = createMemo(() => {
    const q = search().trim().toLowerCase();
    if (!q) return null;
    const out = [];
    const cat = catalog();
    if (cat) {
      for (const t of cat.tables) for (const c of t.columns) {
        if (c.name.toLowerCase().includes(q)) out.push({ type: 'column', name: c.name, sub: `${t.name} · ${c.type}`, table: t.name });
      }
      for (const tr of cat.triggers) {
        if (tr.name.toLowerCase().includes(q) || (tr.statement || '').toLowerCase().includes(q))
          out.push({ type: 'trigger', name: tr.name, sub: `${tr.timing} ${tr.event} · ${tr.table}`, ref: tr });
      }
      for (const r of cat.routines) {
        if (r.name.toLowerCase().includes(q)) out.push({ type: 'routine', name: r.name, sub: r.signature, ref: r });
      }
    }
    for (const f of FUNCTIONS) {
      if (f.name.toLowerCase().includes(q)) out.push({ type: 'func', name: f.name, sub: f.sig, ref: f });
    }
    return out.slice(0, 300);
  });

  const onResult = (r) => {
    if (r.type === 'column') { setManualTable(r.table); setTab('columns'); setSearch(''); }
    else if (r.type === 'trigger') { setTab('triggers'); setSelTrigger(r.ref); setSearch(''); copy(r.ref.statement, 'Trigger definition'); }
    else if (r.type === 'routine') { setTab('routines'); setSelRoutine(r.ref); setSearch(''); copy(r.ref.signature, 'Signature'); }
    else if (r.type === 'func') { setTab('funcs'); setSelFunc(r.ref); setSearch(''); copy(r.ref.sig || `${r.ref.name}()`, 'Signature'); }
  };

  // The header Copy button — context per tab.
  const copyActive = () => {
    if (tab() === 'columns') {
      const t = colTable();
      if (!t) return toast.info('No table selected');
      copy(t.columns.map((c) => c.name).join(', '), 'Columns');
    } else if (tab() === 'triggers') {
      selTrigger() ? copy(selTrigger().statement, 'Trigger definition') : toast.info('Select a trigger first');
    } else if (tab() === 'routines') {
      selRoutine() ? copy(selRoutine().signature, 'Signature') : toast.info('Select a routine first');
    } else {
      selFunc() ? copy(selFunc().sig || `${selFunc().name}()`, 'Signature') : toast.info('Select a function first');
    }
  };

  const TABS = [
    ['columns', 'Columns'], ['triggers', 'Triggers'], ['routines', 'Routines'], ['funcs', 'MySQL fn'],
  ];

  return (
    <FloatingPanel
      open={props.open}
      onClose={() => props.onClose?.()}
      title="🔎 Object Viewer"
      kbd="Ctrl+F12"
      width={430}
      closeOnEsc={false}
      footer={
        <>
          <span class="muted">{connName() ? `${connName()} · ${database()}` : '—'}</span>
          <span class="spacer" />
          <Show when={colTable() && tab() === 'columns' && !results()}>
            <span class="muted">{colTable().columns.length} cols</span>
          </Show>
        </>
      }
    >
        {/* Connection / database (follows the editor; pick to override) */}
        <div class="ov-ctx">
          <select class="input ov-sel" value={String(connId())}
                  onChange={(e) => { setPinned(true); setConnId(e.currentTarget.value); setDatabase(''); loadDbs(e.currentTarget.value); }}>
            <option value="">— connection —</option>
            <For each={conns() || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
          </select>
          <select class="input ov-sel" value={database()}
                  onChange={(e) => { setPinned(true); setDatabase(e.currentTarget.value); }}>
            <option value="">— database —</option>
            <For each={dbList()}>{(n) => <option value={n}>{n}</option>}</For>
          </select>
          <Show when={pinned()}>
            <button class="ov-follow" title="Follow the active editor again" onClick={() => setPinned(false)}>⟲</button>
          </Show>
        </div>

        <input class="input ov-search" placeholder="Search columns, triggers, routines, functions…"
               value={search()} onInput={(e) => setSearch(e.currentTarget.value)} />

        <div class="ov-tabs">
          <For each={TABS}>
            {([id, label]) => (
              <button class="ov-tab" classList={{ active: tab() === id && !search().trim() }}
                      onClick={() => { setSearch(''); setTab(id); }}>{label}</button>
            )}
          </For>
          <span class="spacer" />
          <button class="ov-copy" title="Copy (per tab)" onClick={copyActive}>⧉ Copy</button>
        </div>

        <div class="ov-body">
          <Show when={!connId() || !database()}>
            <div class="ov-hint">Focus a query tab, or pick a connection + database above.</div>
          </Show>
          <Show when={catalog.loading}><div class="ov-hint">Loading…</div></Show>
          <Show when={catalog.error}><div class="ov-hint ov-err">Couldn't load: {String(catalog.error?.message || catalog.error)}</div></Show>

          {/* Search results override the tab content */}
          <Show when={results()}>
            <div class="ov-list">
              <Show when={results().length} fallback={<div class="ov-hint">No matches.</div>}>
                <For each={results()}>
                  {(r) => (
                    <button class="ov-li" onClick={() => onResult(r)}>
                      <span class={`ov-badge ov-badge-${r.type}`}>{r.type}</span>
                      <span class="ov-li-name mono">{r.name}</span>
                      <span class="ov-li-sub">{r.sub}</span>
                    </button>
                  )}
                </For>
              </Show>
            </div>
          </Show>

          <Show when={!results() && catalog()}>
            {/* Columns */}
            <Show when={tab() === 'columns'}>
              <Show when={colTable()} fallback={
                <div class="ov-hint">
                  Put the cursor on a table name in the editor — or pick one:
                  <div class="ov-pick"><For each={catalog().tables}>{(t) => <button class="ov-pick-li mono" onClick={() => setManualTable(t.name)}>{t.name}</button>}</For></div>
                </div>
              }>
                <div class="ov-coltitle">
                  <span class="mono">{colTable().name}</span>
                  <Show when={colTable().view}><span class="ov-badge ov-badge-view">view</span></Show>
                  <span class="spacer" />
                  <Show when={manualTable()}><button class="ov-follow" title="Follow cursor" onClick={() => setManualTable('')}>⟲</button></Show>
                </div>
                <div class="ov-list">
                  <For each={colTable().columns}>
                    {(c) => (
                      <>
                        <div class="ov-col" onClick={() => copy(c.name, 'Column')} title="Click to copy this column">
                          <span class="ov-col-key">{c.pk ? '🔑' : ''}</span>
                          <span class="ov-col-name mono">{c.name}</span>
                          <span class="ov-col-type mono">{c.type}{c.nullable ? '' : ' •'}</span>
                        </div>
                        <Show when={c.enum && c.enum.length}>
                          <div class="ov-enum"><For each={c.enum}>{(v) => <span class="ov-enum-v mono">{v}</span>}</For></div>
                        </Show>
                      </>
                    )}
                  </For>
                </div>
              </Show>
            </Show>

            {/* Triggers */}
            <Show when={tab() === 'triggers'}>
              <Show when={catalog().triggers.length} fallback={<div class="ov-hint">No triggers in this database.</div>}>
                <div class="ov-list">
                  <For each={catalog().triggers}>
                    {(tr) => (
                      <button class="ov-li" classList={{ active: selTrigger()?.name === tr.name }}
                              onClick={() => { setSelTrigger(tr); copy(tr.statement, 'Trigger definition'); }}>
                        <span class="ov-li-name mono">{tr.name}</span>
                        <span class="ov-li-sub">{tr.timing} {tr.event} · {tr.table}</span>
                      </button>
                    )}
                  </For>
                </div>
                <Show when={selTrigger()}><pre class="ov-detail mono">{selTrigger().statement}</pre></Show>
              </Show>
            </Show>

            {/* Routines */}
            <Show when={tab() === 'routines'}>
              <Show when={catalog().routines.length} fallback={<div class="ov-hint">No procedures or functions.</div>}>
                <div class="ov-list">
                  <For each={catalog().routines}>
                    {(r) => (
                      <button class="ov-li" classList={{ active: selRoutine()?.name === r.name }}
                              onClick={() => { setSelRoutine(r); copy(r.signature, 'Signature'); }}>
                        <span class={`ov-badge ov-badge-${r.kind === 'PROCEDURE' ? 'proc' : 'fn'}`}>{r.kind === 'PROCEDURE' ? 'proc' : 'func'}</span>
                        <span class="ov-li-name mono">{r.name}</span>
                      </button>
                    )}
                  </For>
                </div>
                <Show when={selRoutine()}><pre class="ov-detail mono">{selRoutine().signature}</pre></Show>
              </Show>
            </Show>

            {/* MySQL functions */}
            <Show when={tab() === 'funcs'}>
              <div class="ov-list">
                <For each={FUNCTIONS}>
                  {(f) => (
                    <button class="ov-li" classList={{ active: selFunc()?.name === f.name }}
                            onClick={() => { setSelFunc(f); copy(f.sig || `${f.name}()`, 'Signature'); }}>
                      <span class="ov-li-name mono">{f.name}</span>
                      <span class="ov-li-sub mono">{f.sig}</span>
                    </button>
                  )}
                </For>
              </div>
            </Show>
          </Show>
        </div>

    </FloatingPanel>
  );
}
